import { useCallback, useEffect, useState } from "react";
import { Archive, ArrowRight, CalendarSync, Flame, Lock, PackageOpen, ShoppingBasket } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { MobileShell } from "@/components/MobileShell";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import { api, getApiError } from "@/lib/api";


export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadSummary = useCallback(async () => {
    const response = await api.get("/dashboard");
    setSummary(response.data);
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const generateNewPlan = async () => {
    try {
      setBusy(true);
      await api.post("/planner/generate", {});
      await loadSummary();
      toast.success("Nieuw weekmenu staat klaar.");
      navigate("/planner");
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <MobileShell
      title="Vandaag"
      eyebrow={`Welkom ${user?.name || ""}`}
      action={<Button className="rounded-full bg-emerald-700 px-4 text-white hover:bg-emerald-800" onClick={generateNewPlan} disabled={busy} data-testid="dashboard-generate-button">Genereer</Button>}
    >
      <div className="space-y-4">
        <section className="stagger-enter rounded-[28px] border border-stone-200 bg-[linear-gradient(135deg,_rgba(243,230,198,0.96),_rgba(236,245,234,0.98))] p-5" style={{ animationDelay: "80ms" }} data-testid="dashboard-summary-card">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-stone-500" data-testid="dashboard-summary-kicker">
            Huidige week
          </p>
          <h2 className="mt-2 text-2xl font-semibold text-stone-900" data-testid="dashboard-summary-week-start">
            Start {summary?.week_start || "..."}
          </h2>
          <div className="mt-4 grid grid-cols-3 gap-3" data-testid="dashboard-kpi-row">
            <div className="flex h-full min-h-[132px] flex-col justify-between rounded-[22px] bg-white/85 p-4" data-testid="dashboard-lock-card">
              <Lock className="h-5 w-5 text-emerald-700" />
              <p className="mt-3 text-2xl font-semibold text-stone-900" data-testid="dashboard-lock-value">{summary?.locked_count ?? 0}</p>
              <p className="min-h-[2.5rem] text-xs leading-snug text-stone-500 sm:text-sm" data-testid="dashboard-lock-label">gelockte meals</p>
            </div>
            <div className="flex h-full min-h-[132px] flex-col justify-between rounded-[22px] bg-white/85 p-4" data-testid="dashboard-grocery-card">
              <ShoppingBasket className="h-5 w-5 text-orange-600" />
              <p className="mt-3 text-2xl font-semibold text-stone-900" data-testid="dashboard-grocery-value">{summary?.grocery_item_count ?? 0}</p>
              <p className="min-h-[2.5rem] text-xs leading-snug text-stone-500 sm:text-sm" data-testid="dashboard-grocery-label">boodschappen</p>
            </div>
            <div className="flex h-full min-h-[132px] flex-col justify-between rounded-[22px] bg-white/85 p-4" data-testid="dashboard-leftover-card">
              <Archive className="h-5 w-5 text-stone-700" />
              <p className="mt-3 text-2xl font-semibold text-stone-900" data-testid="dashboard-leftover-value">{summary?.leftover_count ?? 0}</p>
              <p className="min-h-[2.5rem] text-xs leading-snug text-stone-500 sm:text-sm" data-testid="dashboard-leftover-label">leftovers</p>
            </div>
          </div>
        </section>

        <section className="stagger-enter rounded-[28px] border border-stone-200 bg-white/90 p-5" style={{ animationDelay: "150ms" }} data-testid="dashboard-focus-card">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-stone-500" data-testid="dashboard-focus-kicker">
            Today focus
          </p>
          <h3 className="mt-2 text-xl font-semibold text-stone-900" data-testid="dashboard-focus-title">
            {summary?.today_focus?.recipe?.title || "Weekmenu laden..."}
          </h3>
          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            <span className="rounded-full bg-orange-100 px-3 py-1 text-orange-700" data-testid="dashboard-focus-kcal">
              <Flame className="mr-1 inline h-3 w-3" />
              {summary?.today_focus?.recipe?.kcal_per_serving || 0} kcal
            </span>
            <span className="rounded-full bg-emerald-100 px-3 py-1 text-emerald-800" data-testid="dashboard-focus-carbs">
              {summary?.today_focus?.recipe?.carbs_per_serving || 0} g carbs
            </span>
            <span className="rounded-full bg-stone-100 px-3 py-1 text-stone-700" data-testid="dashboard-focus-slot">
              {summary?.today_focus?.slot_label || "Diner"}
            </span>
          </div>
          <p className="mt-4 text-sm text-stone-600" data-testid="dashboard-focus-description">
            {summary?.today_focus?.recipe?.description || "Smartly bereidt je week voor op compacte, logische maaltijden."}
          </p>
        </section>

        <div className="stagger-enter grid grid-cols-1 gap-3" style={{ animationDelay: "220ms" }}>
          <Button className="h-12 justify-between rounded-full bg-stone-900 text-white hover:bg-stone-800" onClick={() => navigate("/planner")} data-testid="dashboard-open-planner-button">
            Open weekplanner
            <ArrowRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" className="h-12 justify-between rounded-full border-stone-200 bg-white" onClick={generateNewPlan} disabled={busy} data-testid="dashboard-refresh-plan-button">
            Genereer nieuw weekmenu
            <CalendarSync className="h-4 w-4" />
          </Button>
          <Button variant="outline" className="h-12 justify-between rounded-full border-stone-200 bg-white" onClick={() => navigate("/pantry")} data-testid="dashboard-open-pantry-button">
            Open pantry
            <PackageOpen className="h-4 w-4" />
          </Button>
          <Button variant="outline" className="h-12 justify-between rounded-full border-stone-200 bg-white" onClick={() => navigate("/leftovers")} data-testid="dashboard-open-leftovers-button">
            Open leftovers
            <Archive className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </MobileShell>
  );
}